import axios from "axios";
import { getAuthApiRoute } from "@/helpers/api.util";

function readPositiveLimit(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export const PUBLIC_AUTH_CONFIG_DEFAULTS = {
  maxProfileCount: readPositiveLimit(
    process.env.NEXT_PUBLIC_AUTH_MAX_PROFILE_COUNT,
    3
  ),
  maxSessionCount: readPositiveLimit(
    process.env.NEXT_PUBLIC_AUTH_MAX_SESSION_COUNT,
    3
  ),
  serverTime: null,
};

const CONFIG_STORAGE_KEY = "frontendClientPublicAuthConfig";
const CONFIG_CACHE_TTL_MS = 5 * 60 * 1000;

let currentConfig = readStoredConfig();
let serverClockOffsetMs = 0;
let hasServerClock = false;
let lastFetchAt = 0;
let pendingRequest = null;
let subscribers = [];

function hasBrowserStorage() {
  try {
    return typeof window !== "undefined" && Boolean(window.localStorage);
  } catch (error) {
    return false;
  }
}

function readStoredConfig() {
  if (!hasBrowserStorage()) return { ...PUBLIC_AUTH_CONFIG_DEFAULTS };

  try {
    const raw = window.localStorage.getItem(CONFIG_STORAGE_KEY);
    if (!raw) return { ...PUBLIC_AUTH_CONFIG_DEFAULTS };

    return normalizeConfig(JSON.parse(raw));
  } catch (error) {
    return { ...PUBLIC_AUTH_CONFIG_DEFAULTS };
  }
}

function persistConfig(config) {
  if (!hasBrowserStorage()) return;

  try {
    window.localStorage.setItem(
      CONFIG_STORAGE_KEY,
      JSON.stringify({
        maxProfileCount: config.maxProfileCount,
        maxSessionCount: config.maxSessionCount,
      })
    );
  } catch (error) {
    // Storage is best-effort; the in-memory clock offset remains authoritative.
  }
}

function toPositiveNumber(value, fallback) {
  const normalized = Number(value);
  if (!Number.isFinite(normalized) || normalized <= 0) return fallback;
  return normalized;
}

function normalizeConfig(config) {
  const source = config || {};

  return {
    maxProfileCount: toPositiveNumber(
      source.maxProfileCount,
      PUBLIC_AUTH_CONFIG_DEFAULTS.maxProfileCount
    ),
    maxSessionCount: toPositiveNumber(
      source.maxSessionCount,
      PUBLIC_AUTH_CONFIG_DEFAULTS.maxSessionCount
    ),
    serverTime: Number.isFinite(Number(source.serverTime))
      ? Number(source.serverTime)
      : null,
  };
}

function notifySubscribers() {
  const snapshot = {
    config: getPublicAuthConfigSync(),
    clockOffsetMs: serverClockOffsetMs,
    hasServerClock,
  };

  subscribers.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (error) {
      // Keep one listener from breaking countdowns on other surfaces.
    }
  });
}

function applyConfig(rawConfig, requestStartedAt, responseReceivedAt) {
  const nextConfig = normalizeConfig(rawConfig);
  currentConfig = nextConfig;
  lastFetchAt = responseReceivedAt || Date.now();

  if (nextConfig.serverTime !== null) {
    const midpoint = requestStartedAt
      ? Math.round((requestStartedAt + lastFetchAt) / 2)
      : lastFetchAt;
    serverClockOffsetMs = nextConfig.serverTime - midpoint;
    hasServerClock = true;
  }

  persistConfig(nextConfig);
  notifySubscribers();

  return getPublicAuthConfigSync();
}

export function getPublicAuthConfigSync() {
  return {
    ...PUBLIC_AUTH_CONFIG_DEFAULTS,
    ...currentConfig,
  };
}

export function getServerNow() {
  return Date.now() + serverClockOffsetMs;
}

export function getClockOffsetMs() {
  return serverClockOffsetMs;
}

export function isServerClockSynced() {
  return hasServerClock;
}

export function subscribePublicAuthConfig(listener) {
  subscribers.push(listener);

  return function unsubscribe() {
    subscribers = subscribers.filter((item) => item !== listener);
  };
}

export async function fetchPublicAuthConfig(options) {
  const force = options?.force;
  const now = Date.now();

  if (!force && hasServerClock && now - lastFetchAt < CONFIG_CACHE_TTL_MS) {
    return getPublicAuthConfigSync();
  }

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestStartedAt = Date.now();

  pendingRequest = axios
    .get(getAuthApiRoute("config"))
    .then((response) => {
      const responseReceivedAt = Date.now();
      const responseData = response.data || {};
      const data = responseData.data || responseData;
      return applyConfig(data, requestStartedAt, responseReceivedAt);
    })
    .catch(() => getPublicAuthConfigSync());

  pendingRequest = pendingRequest.then(
    (config) => {
      pendingRequest = null;
      return config;
    },
    (error) => {
      pendingRequest = null;
      throw error;
    }
  );

  return pendingRequest;
}

export function __setPublicAuthConfigForTests(config, clockOffset) {
  currentConfig = normalizeConfig(config);
  serverClockOffsetMs = Number(clockOffset) || 0;
  hasServerClock = currentConfig.serverTime !== null || serverClockOffsetMs !== 0;
  notifySubscribers();
}
